import { A } from '@solidjs/router';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import PublicLayout from '../PublicLayout';

export default function GrowthTrapPostVi() {
  const [heroFailed, setHeroFailed] = createSignal(false);
  const [barcodeImageFailed, setBarcodeImageFailed] = createSignal(false);
  const [chartFailed, setChartFailed] = createSignal(false);

  createEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline:
        'Bẫy Tăng Trưởng: Vì Sao Hệ Thống Thủ Công Của Bạn Sẽ Sụp Đổ Ở Mức 50 Đơn Hàng Mỗi Ngày',
      description:
        'Điều hoạt động tốt với 5 đơn hàng mỗi ngày sẽ trở thành cơn ác mộng ở mức 50. Tìm hiểu về "nợ kỹ thuật" trong kho hàng, tại sao quét mã vạch là bước nhảy vọt lớn nhất về độ chính xác, và cách kiểm tra áp lực 60 giây để biết bạn có sẵn sàng mở rộng quy mô hay không.',
      datePublished: '2026-05-08',
      dateModified: '2026-05-08',
      image: 'https://khoai.io/blog-scaling-chaos.jpg',
      url: 'https://khoai.io/vi/blogs/growth-trap',
      inLanguage: 'vi',
      author: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://khoai.io/og-welcome.jpg',
        },
      },
      isPartOf: {
        '@type': 'Blog',
        name: 'Khoai Blog',
        url: 'https://khoai.io/blogs',
      },
    };

    let script = document.getElementById(
      'ld-json-blog-growth-trap-vi'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-blog-growth-trap-vi';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    let bcScript = document.getElementById(
      'ld-json-blog-growth-trap-vi-bc'
    ) as HTMLScriptElement | null;
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.type = 'application/ld+json';
      bcScript.id = 'ld-json-blog-growth-trap-vi-bc';
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Trang chủ',
          item: 'https://khoai.io',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://khoai.io/vi/blogs',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: schema.headline,
          item: schema.url,
        },
      ],
    });
  });

  onCleanup(() => {
    document.getElementById('ld-json-blog-growth-trap-vi')?.remove();
    document.getElementById('ld-json-blog-growth-trap-vi-bc')?.remove();
  });

  const h2 = 'mt-12 text-2xl font-extrabold text-gray-900 sm:text-3xl';
  const p = 'mt-4 text-base leading-8 text-gray-600';
  const ul = 'mt-4 list-disc space-y-2 pl-6 text-base leading-7 text-gray-600';
  const imgFallback =
    'flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400';

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav class="mb-8 flex items-center gap-2 text-sm text-gray-400">
          <A href="/vi/blogs" class="hover:text-amber-600">
            Blog
          </A>
          <span>/</span>
          <span class="text-gray-600">Chiến lược vận hành</span>
        </nav>

        {/* Header */}
        <header>
          <span class="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700">
            Chiến lược vận hành
          </span>
          <h1 class="mt-4 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
            Bẫy Tăng Trưởng: Vì Sao Hệ Thống Thủ Công Của Bạn Sẽ Sụp Đổ Ở Mức 50
            Đơn Hàng Mỗi Ngày
          </h1>
          <p class="mt-4 text-lg leading-relaxed text-gray-500">
            Điều hoạt động tốt với 5 đơn hàng mỗi ngày sẽ trở thành cơn ác mộng
            ở mức 50. Đây là câu chuyện về sự chuyển dịch từ doanh nghiệp "phụ
            thuộc vào người" sang doanh nghiệp "phụ thuộc vào quy trình" — và
            tại sao bạn cần thực hiện trước khi sự tăng trưởng buộc bạn phải
            làm.
          </p>
          <div class="mt-5 flex items-center gap-4 text-sm text-gray-400">
            <span>8 tháng 5, 2026</span>
            <span>·</span>
            <span>8 phút đọc</span>
          </div>
        </header>

        {/* Hero image */}
        <div class="mt-10 overflow-hidden rounded-2xl">
          <Show
            when={!heroFailed()}
            fallback={
              <div class={imgFallback} style={{ 'min-height': '340px' }}>
                Màn hình chia đôi: bên trái là bàn làm việc lộn xộn với đống đơn
                hàng giấy; bên phải là giao diện tablet sạch sẽ với danh sách
                nhặt hàng rõ ràng
              </div>
            }
          >
            <img
              src="/blog-scaling-chaos.jpg"
              alt="Màn hình chia đôi: bên trái là bàn làm việc lộn xộn với đống đơn hàng giấy; bên phải là giao diện tablet sạch sẽ với danh sách nhặt hàng rõ ràng"
              width={1200}
              height={628}
              class="w-full object-cover"
              onError={() => setHeroFailed(true)}
            />
          </Show>
        </div>

        {/* ── Body ── */}
        <div class="mt-12">
          {/* Hook: Monday Morning Chaos */}
          <p class={p}>
            7 giờ sáng thứ Hai. Đội của bạn vừa bước vào kho. Cuối tuần vừa rồi
            có hơn 50 đơn hàng mới đổ vào — nhiều hơn cả tuần bình thường của
            tháng trước. Không ai biết bắt đầu từ đâu. Danh sách nhặt hàng được
            in từ tối qua đã lỗi thời vì có thêm 12 đơn nữa vừa vào lúc nửa đêm.
            Nhân viên kho duy nhất biết hàng để ở đâu vừa nghỉ ốm.
          </p>
          <p class={p}>
            Đây không phải là tình huống đặc biệt. Đây là{' '}
            <strong class="font-semibold text-gray-900">điểm gãy</strong> — thời
            khắc hệ thống vận hành của bạn, vốn hoạt động tốt ở quy mô nhỏ, đột
            ngột không còn đủ sức chịu đựng nữa.
          </p>
          <p class={p}>
            Điều đáng sợ là điểm gãy này đến sớm hơn bạn nghĩ, và thường đến
            ngay giữa giai đoạn tăng trưởng đẹp nhất.
          </p>

          {/* Section 1: Brain Drain */}
          <h2 class={h2}>Vấn Đề "Kiến Thức Nằm Trong Đầu Người"</h2>
          <p class={p}>
            Trong các đội nhỏ, thường có một người biết mọi thứ: hàng SKU 0047
            để ở góc trái kho B, nhà cung cấp X hay giao thiếu 5%, và khách hàng
            Y chỉ nhận hàng vào buổi chiều. Kiến thức đó không được ghi lại ở
            đâu — nó tồn tại trong đầu người đó.
          </p>
          <p class={p}>
            Khi quy mô còn nhỏ, điều này hoạt động được. Khi bạn có 50 đơn hàng
            mỗi ngày, 300 SKU đang hoạt động, và 4 nhân viên kho phải phối hợp
            đồng thời — nó sụp đổ ngay khi người đó nghỉ ốm một ngày.
          </p>

          {/* Signs You're Already There */}
          <div class="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-6">
            <p class="text-sm font-bold uppercase tracking-wide text-gray-500">
              Dấu hiệu bạn đang ở đây rồi
            </p>
            <ul class="mt-3 space-y-2 text-base leading-7 text-gray-700">
              <li>✗ Bạn đã mất một đơn hàng trong tháng này</li>
              <li>
                ✗ Khi một nhân viên chủ chốt nghỉ, hoạt động chậm lại rõ rệt
              </li>
              <li>
                ✗ Bạn không thể trả lời ngay lập tức khi khách hỏi "còn hàng
                không?"
              </li>
              <li>✗ Cuối tháng mất 1–2 ngày để đối soát số liệu tồn kho</li>
              <li>✗ Bạn đã từng giao nhầm hàng vì nhầm vị trí kệ</li>
            </ul>
          </div>

          <p class={p}>
            Giải pháp không phải là tìm người giỏi hơn. Giải pháp là{' '}
            <strong class="font-semibold text-gray-900">
              đưa kiến thức đó ra khỏi đầu người và vào hệ thống
            </strong>
            . Gán nhãn vị trí kệ (A-01-3, B-12-2). Tạo quy trình nhặt hàng
            chuẩn. Xây dựng "nguồn dữ liệu duy nhất" mà bất kỳ ai cũng có thể
            tra cứu — kể cả người mới vào làm hôm nay.
          </p>

          {/* Section 2: Error Multiplier */}
          <h2 class={h2}>
            Toán Học Của Sai Sót: Tỷ Lệ 2% Nghe Nhỏ, Cho Đến Khi Bạn Tính Ra
          </h2>
          <p class={p}>
            Khi bạn xử lý 5 đơn hàng mỗi ngày, tỷ lệ sai sót 2% có nghĩa là
            khoảng một lần giao nhầm mỗi 10 ngày. Khó chịu, nhưng có thể xử lý
            được bằng lời xin lỗi và giao lại.
          </p>
          <p class={p}>
            Khi bạn xử lý 50 đơn hàng mỗi ngày với cùng tỷ lệ sai sót đó: đó là
            một khách hàng tức giận mỗi ngày. Không phải mỗi tuần, mỗi ngày.
            Nhân với chi phí hoàn hàng, chi phí giao lại, thiệt hại uy tín — và
            đột nhiên "tỷ lệ sai sót nhỏ" trở thành vấn đề kinh doanh thực sự.
          </p>
          <p class={p}>
            Điều làm cho kho hàng thủ công dễ mắc lỗi không phải là con người —
            mà là{' '}
            <strong class="font-semibold text-gray-900">
              số lần quyết định phải được đưa ra bằng mắt thường
            </strong>
            : đọc nhãn, so sánh mã SKU, kiểm tra vị trí kệ, xác nhận số lượng.
            Mỗi bước đó là một cơ hội sai sót. Khi khối lượng tăng gấp 10 lần,
            cơ hội sai sót tăng gấp 10 lần theo.
          </p>

          {/* Section 3: Barcode */}
          <h2 class={h2}>
            Quét Mã Vạch: Không Phải Công Nghệ Xa Xỉ — Đây Là Bước Nhảy Vọt Lớn
            Nhất Về Độ Chính Xác
          </h2>
          <p class={p}>
            Nhiều chủ kho nghĩ rằng quét mã vạch đòi hỏi máy quét chuyên dụng
            đắt tiền và hệ thống phức tạp. Thực tế không phải vậy. Khoai hoạt
            động với camera điện thoại thông thường — không cần thêm phần cứng
            nào.
          </p>
          <p class={p}>
            Sự khác biệt giữa "đọc nhãn bằng mắt" và "quét mã vạch" nghe có vẻ
            nhỏ, nhưng về mặt độ chính xác thì khổng lồ:
          </p>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                Đọc nhãn bằng mắt:
              </strong>{' '}
              tỷ lệ lỗi của con người ở mức 1–3%, đặc biệt khi mệt mỏi hoặc vội
              vàng
            </li>
            <li>
              <strong class="font-semibold text-gray-900">Quét mã vạch:</strong>{' '}
              tỷ lệ lỗi dưới 0.01% — gần như bằng không
            </li>
          </ul>
          <p class={p}>
            Khi bạn quét mã vạch, hệ thống xác nhận ngay: đúng sản phẩm, đúng số
            lượng, đúng vị trí kệ. Không có chỗ cho "tôi tưởng là..." hoặc "nhìn
            giống nhau quá". Phần mềm bắt lỗi trước khi đơn hàng rời khỏi kho.
          </p>

          {/* Barcode image */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!barcodeImageFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Tay cầm điện thoại quét mã vạch trên nhãn kệ hàng trong kho
                </div>
              }
            >
              <img
                src="/blog-barcode-scanning.jpg"
                alt="Tay cầm điện thoại thông minh quét mã vạch chuyên nghiệp trên kệ hàng trong kho"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setBarcodeImageFailed(true)}
              />
            </Show>
          </div>

          {/* Section 4: Messy Middle */}
          <h2 class={h2}>
            "Vùng Giữa Lộn Xộn": Quá Lớn Cho Bảng Tính, Cảm Thấy Quá Nhỏ Cho
            Phần Mềm Doanh Nghiệp
          </h2>
          <p class={p}>
            Đây là giai đoạn khó nhất: bạn đang xử lý 30–60 đơn hàng mỗi ngày,
            bảng tính đã bắt đầu gây đau đầu, nhưng bạn vẫn cảm thấy mình chưa
            đủ lớn để cần "hệ thống thực sự".
          </p>
          <p class={p}>
            Đây chính xác là thời điểm mà hầu hết các doanh nghiệp mắc bẫy. Họ
            chờ đến khi đau đủ để hành động — nhưng đến lúc đó, họ đã mất khách
            hàng, mất nhân viên bị kiệt sức, và mất hàng tháng dữ liệu lộn xộn
            cần dọn dẹp.
          </p>

          {/* Khoai callout */}
          <div class="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <p class="text-base leading-8 text-gray-700">
              <strong class="font-semibold text-gray-900">
                Khoai được xây dựng chính xác cho giai đoạn này.
              </strong>{' '}
              Không phải phần mềm doanh nghiệp cồng kềnh với hàng trăm tính năng
              bạn không cần. Không phải bảng tính mong manh sẽ gãy khi bạn cần
              nó nhất. Khoai bắt đầu hoạt động trong vài giờ, không cần tư vấn
              triển khai, không cần hợp đồng dài hạn — và nó mở rộng cùng bạn từ
              30 đến 300 đơn hàng mỗi ngày mà không cần thay đổi hệ thống.
            </p>
          </div>

          {/* Section 5: Stress Test */}
          <h2 class={h2}>Kiểm Tra Áp Lực 60 Giây</h2>
          <p class={p}>
            Đây là cách đơn giản nhất để biết hệ thống vận hành của bạn có sẵn
            sàng mở rộng hay không:
          </p>
          <div class="mt-6 rounded-2xl border-2 border-amber-400 bg-amber-50 p-6">
            <p class="text-lg font-bold text-gray-900">
              Thử thách: Liệu một người lạ — ai đó chưa bao giờ đặt chân vào kho
              của bạn — có thể tìm thấy một SKU cụ thể trong vòng 60 giây không?
            </p>
            <p class="mt-3 text-base text-gray-600">
              Nếu câu trả lời là "không" hoặc "chỉ khi có [tên nhân viên] ở đây"
              — bạn đang vận hành một doanh nghiệp phụ thuộc vào người, không
              phải quy trình. Và điều đó sẽ gây ra vấn đề khi bạn tăng trưởng.
            </p>
          </div>
          <p class={p}>
            Nếu hệ thống của bạn vượt qua bài kiểm tra này — vị trí kệ được gán
            nhãn rõ ràng, danh mục sản phẩm có thể tra cứu ngay, bất kỳ ai cũng
            có thể nhặt hàng từ danh sách — thì bạn đang xây dựng một hoạt động
            có thể mở rộng quy mô. Nếu không, bạn đang xây dựng một cái bẫy cho
            chính mình.
          </p>

          {/* Growth chart image */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!chartFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Biểu đồ tăng trưởng với đường mũi tên đi qua "Điểm Gãy" rồi
                  tiếp tục lên với nhãn "Được Tối Ưu Hóa"
                </div>
              }
            >
              <img
                src="/blog-growth-chart.jpg"
                alt="Biểu đồ sạch sẽ hiển thị mũi tên tăng trưởng đi qua đường Điểm Gãy tại 50 đơn hàng/ngày, tiếp tục lên cao với nhãn Được Tối Ưu Hóa"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setChartFailed(true)}
              />
            </Show>
          </div>

          {/* Section 6: SOPs */}
          <h2 class={h2}>
            Phần Mềm Không Chỉ Là Công Cụ — Nó Buộc Bạn Xây Dựng Quy Trình
          </h2>
          <p class={p}>
            Một trong những lợi ích ít được nói đến nhất của việc chuyển sang
            phần mềm quản lý kho là nó buộc bạn phải chuẩn hóa. Khi bạn nhập sản
            phẩm vào hệ thống, bạn phải quyết định: SKU này được gọi là gì? Nó
            nằm ở đâu? Đơn vị đo lường là gì? Ngưỡng đặt hàng lại là bao nhiêu?
          </p>
          <p class={p}>
            Những quyết định đó — khi được ghi lại trong hệ thống — trở thành
            quy trình vận hành chuẩn (SOP) của bạn. Không phải tài liệu ai đó
            soạn rồi không ai đọc. Mà là quy trình sống, được thực thi mỗi lần
            ai đó quét mã vạch hoặc xác nhận đơn hàng.
          </p>
          <p class={p}>
            Đó là cách bạn chuyển từ doanh nghiệp "phụ thuộc vào người" sang
            doanh nghiệp "phụ thuộc vào quy trình". Và chỉ khi đạt được điều đó,
            bạn mới thực sự sẵn sàng để tăng trưởng.
          </p>

          {/* CTA */}
          <div class="mt-14 rounded-2xl bg-amber-600 px-8 py-10 text-center text-white">
            <h2 class="text-2xl font-extrabold">
              Bạn đang ở "vùng giữa"? Hãy thoát ra ngay hôm nay.
            </h2>
            <p class="mt-3 text-base leading-7 text-amber-100">
              Khoai giúp bạn xây dựng nguồn dữ liệu duy nhất, gán nhãn vị trí
              kệ, quét mã vạch bằng điện thoại, và xử lý 300 đơn hàng mỗi ngày
              với cùng đội ngũ bạn có hôm nay. Bắt đầu miễn phí, không cần thẻ
              tín dụng.
            </p>
            <A
              href="/signup"
              class="mt-6 inline-block rounded-xl bg-white px-8 py-3 text-base font-bold text-amber-700 hover:bg-amber-50"
            >
              Dùng thử Khoai miễn phí →
            </A>
          </div>
        </div>

        {/* Back to blog */}
        <div class="mt-16 border-t border-gray-200 pt-8">
          <A
            href="/vi/blogs"
            class="text-sm font-semibold text-amber-600 hover:text-amber-700"
          >
            ← Quay lại tất cả bài viết
          </A>
        </div>
      </article>
    </PublicLayout>
  );
}
